import { apiClient } from '@/lib/api-client';
import { RemoveAvatarResponse, UserAvatarResponse } from '@/types/avatar';
import { normalizeAvatarResponse } from '@/utils/avatar';

const getAvatarEndpoint = (username: string) => `/users/users/${username}/avatar`;
const ACCEPTED_AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const removedAvatarPayload: UserAvatarResponse = {
  avatar_upload_id: null,
  status: 'REMOVED',
  moderation_status: 'SKIPPED',
  rejection_reason: null,
  is_current: false,
  created_at: null,
  processed_at: null,
  avatar_url: null,
  avatar_url_48: null,
  avatar_url_128: null,
  avatar_url_256: null,
};

const removedAvatarResponsePayload: RemoveAvatarResponse = {
  status: 'removed',
  avatar_url: null,
  avatar_url_48: null,
  avatar_url_128: null,
  avatar_url_256: null,
};

const isNullableString = (value: unknown) => value === null || typeof value === 'string';
const isBoolean = (value: unknown) => typeof value === 'boolean';

const isUserAvatarJsonPayload = (value: unknown): value is UserAvatarResponse =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  typeof (value as UserAvatarResponse).status === 'string' &&
  isNullableString((value as UserAvatarResponse).avatar_upload_id) &&
  isNullableString((value as UserAvatarResponse).moderation_status) &&
  isNullableString((value as UserAvatarResponse).rejection_reason) &&
  isBoolean((value as UserAvatarResponse).is_current) &&
  isNullableString((value as UserAvatarResponse).created_at) &&
  isNullableString((value as UserAvatarResponse).processed_at) &&
  isNullableString((value as UserAvatarResponse).avatar_url) &&
  isNullableString((value as UserAvatarResponse).avatar_url_48) &&
  isNullableString((value as UserAvatarResponse).avatar_url_128) &&
  isNullableString((value as UserAvatarResponse).avatar_url_256);

const isRemoveAvatarResponse = (value: unknown): value is RemoveAvatarResponse =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (value as RemoveAvatarResponse).status === 'removed' &&
  (value as RemoveAvatarResponse).avatar_url === null &&
  (value as RemoveAvatarResponse).avatar_url_48 === null &&
  (value as RemoveAvatarResponse).avatar_url_128 === null &&
  (value as RemoveAvatarResponse).avatar_url_256 === null;

export const fetchAvatar = async (username: string) => {
  const response = await apiClient.get<unknown>(getAvatarEndpoint(username), {
    headers: { Accept: 'application/json' },
  });

  if (!isUserAvatarJsonPayload(response.data)) {
    return normalizeAvatarResponse(removedAvatarPayload);
  }

  return normalizeAvatarResponse(response.data);
};

export const uploadAvatar = async (username: string, file: Blob | File) => {
  if (!ACCEPTED_AVATAR_TYPES.has(file.type)) {
    throw new Error('Unsupported avatar file type.');
  }

  const formData = new FormData();
  const fileName = file instanceof File ? file.name : 'avatar-upload';
  formData.append('avatar', file, fileName);

  const response = await apiClient.put<unknown>(getAvatarEndpoint(username), formData);
  if (!isUserAvatarJsonPayload(response.data)) {
    return normalizeAvatarResponse(removedAvatarPayload);
  }

  return normalizeAvatarResponse(response.data);
};

export const removeAvatar = async (username: string) => {
  const response = await apiClient.put<unknown>(getAvatarEndpoint(username), { remove: true });
  if (!isRemoveAvatarResponse(response.data)) {
    return removedAvatarResponsePayload;
  }

  return response.data;
};
